import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Switch,
  InputNumber,
  Input,
  Modal,
  Popconfirm,
  Card,
  App,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../api';
import { t } from '../i18n';
import { FirmwarePublishButton } from '../components/FirmwarePublishModal';
import { fmtDateTime } from '../time';

type Release = Awaited<ReturnType<typeof api.listFirmwareReleases>>[number];

const fmtBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};
const fmtTs = fmtDateTime;

/** Edit a release's metadata. Mounted with key={release.id} so its state seeds
 * from the row. Changing mandatory/min-proto re-signs the release server-side. */
function EditReleaseModal({ release, onClose }: { release: Release; onClose: () => void }) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [mandatory, setMandatory] = useState(release.mandatory);
  const [minProto, setMinProto] = useState(release.minProtocolVersion);
  const [notes, setNotes] = useState(release.releaseNotes ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.updateFirmwareRelease(release.id, {
        mandatory,
        minProtocolVersion: minProto,
        releaseNotes: notes,
      }),
    onSuccess: () => {
      message.success(t('fwSaved'));
      qc.invalidateQueries({ queryKey: ['firmware-releases'] });
      onClose();
    },
    onError: (e) => message.error((e as Error).message),
  });

  return (
    <Modal
      open
      title={t('fwEditTitle', { version: release.version })}
      okText={t('fwSaveBtn')}
      onOk={() => save.mutate()}
      confirmLoading={save.isPending}
      onCancel={onClose}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space>
          <Switch checked={mandatory} onChange={setMandatory} />
          <Typography.Text>{t('fwMandatory')}</Typography.Text>
        </Space>
        <div>
          <Typography.Text type="secondary">{t('fwMinProto')}</Typography.Text>
          <div style={{ marginTop: 4 }}>
            <InputNumber min={1} value={minProto} onChange={(v) => setMinProto(v ?? 1)} />
          </div>
        </div>
        <div>
          <Typography.Text type="secondary">{t('fwNotes')}</Typography.Text>
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </div>
      </Space>
    </Modal>
  );
}

/** Superadmin: manage published firmware releases — list, edit metadata, delete,
 * and publish new ones. */
export function Firmware() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [editing, setEditing] = useState<Release | null>(null);
  const q = useQuery({ queryKey: ['firmware-releases'], queryFn: api.listFirmwareReleases });

  const del = useMutation({
    mutationFn: (id: string) => api.deleteFirmwareRelease(id),
    onSuccess: () => {
      message.success(t('fwDeleted'));
      qc.invalidateQueries({ queryKey: ['firmware-releases'] });
    },
    onError: (e) => message.error((e as Error).message),
  });

  const columns: ColumnsType<Release> = [
    {
      title: t('fwVersion'),
      dataIndex: 'version',
      render: (v: string, r) => (
        <Space size={6}>
          <Typography.Text code strong>{v}</Typography.Text>
          {r.mandatory && <Tag color="red">{t('deviceUpdateMandatoryTag')}</Tag>}
        </Space>
      ),
    },
    { title: t('fwSize'), dataIndex: 'binarySize', render: (n: number) => fmtBytes(n) },
    { title: t('fwMinProto'), dataIndex: 'minProtocolVersion' },
    { title: t('fwPublishedAt'), dataIndex: 'publishedAt', render: (u: number) => <Typography.Text type="secondary">{fmtTs(u)}</Typography.Text> },
    {
      title: t('fwNotes'),
      dataIndex: 'releaseNotes',
      render: (n: string | null) => (
        <Typography.Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{n || '—'}</Typography.Text>
      ),
    },
    {
      title: t('colActions'),
      key: 'actions',
      render: (_: unknown, r) => (
        <Space wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(r)}>
            {t('fwEditBtn')}
          </Button>
          <Popconfirm title={t('fwDeleteConfirm', { version: r.version })} onConfirm={() => del.mutate(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} loading={del.isPending && del.variables === r.id}>
              {t('fwDeleteBtn')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={t('fwPageTitle')}
        extra={<FirmwarePublishButton />}
        style={{ marginBottom: 16 }}
      >
        <Table<Release>
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={q.data ?? []}
          loading={q.isLoading}
          locale={{ emptyText: t('fwEmpty') }}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>
      {editing && <EditReleaseModal key={editing.id} release={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
